import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { User, Check, X, Clock, Trash2, ChevronRight, Filter, UserX, MessageSquare, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function CoachPlayers() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [birthYear, setBirthYear] = useState('all');
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [birthYears, setBirthYears] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { loadPlayers(); }, [birthYear, showIncomplete, showInactive]);

  const loadPlayers = async () => {
    try {
      const res = await adminAPI.players(birthYear, showIncomplete, showInactive);
      setPlayers(res.data || []);

      // Extract unique birth years for filter
      const years = [...new Set(
        (res.data || [])
          .map(p => p.profile?.birth_year)
          .filter(Boolean)
      )].sort();
      if (years.length > 0) setBirthYears(years);
    } catch (err) {
      toast.error('Failed to load players');
    }
    setLoading(false);
  };

  const toggleSub = async (userId, current) => {
    try {
      await adminAPI.updateSubscription(userId, !current);
      toast.success(current ? 'Subscription deactivated' : 'Subscription activated');
      loadPlayers();
    } catch { toast.error('Failed'); }
  };

  const deletePlayer = async (userId, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await adminAPI.deletePlayer(userId);
      toast.success(`${name} deleted`);
      loadPlayers();
    } catch { toast.error('Failed to delete'); }
  };

  const resetPlayer = async (userId, name) => {
    const ok = window.confirm(
      `RESET ${name?.toUpperCase() || 'this player'} to Week 1?\n\nThis permanently deletes:\n• All drill progress (leaderboard stats)\n• Streak & badges\n• Feedback submissions\n• Weekly Challenge submissions\n\nProfile, devices, subscription stay intact. Cannot be undone.`
    );
    if (!ok) return;
    try {
      const res = await adminAPI.resetPlayer(userId);
      toast.success(res.data?.message || `${name} reset to Week 1`);
      loadPlayers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  return (
    <Layout title="PLAYERS" showBack>
      <div className="px-4 py-6 space-y-4">
        <div>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight text-[#0A2242]" data-testid="players-title">MANAGE PLAYERS</h2>
          <p className="text-xs text-[#94A3B8] mt-1">{players.length} player{players.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Birth Year Filter */}
          <div className="flex items-center gap-1.5 bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 shadow-sm">
            <Filter size={12} className="text-[#94A3B8]" />
            <select
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              className="text-xs font-bold text-[#0A2242] bg-transparent border-none outline-none cursor-pointer"
              data-testid="birth-year-filter"
            >
              <option value="all">All Years</option>
              {birthYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Show Incomplete Toggle */}
          <button
            onClick={() => setShowIncomplete(!showIncomplete)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
              showIncomplete
                ? 'bg-[#FF9100]/10 text-[#FF9100] border border-[#FF9100]/30'
                : 'bg-white text-[#94A3B8] border border-[#E2E8F0] shadow-sm'
            }`}
            data-testid="show-incomplete-toggle"
          >
            <UserX size={12} />
            {showIncomplete ? 'Showing Incomplete' : 'Show Incomplete'}
          </button>

          {/* Show Inactive (unpaid) Toggle */}
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
              showInactive
                ? 'bg-red-500/10 text-red-500 border border-red-500/30'
                : 'bg-white text-[#94A3B8] border border-[#E2E8F0] shadow-sm'
            }`}
            data-testid="show-inactive-toggle"
          >
            <X size={12} />
            {showInactive ? 'Showing Inactive' : 'Show Inactive'}
          </button>
        </div>

        {/* Search bar */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search players by name, email, team..."
            className="w-full bg-white border border-[#E2E8F0] rounded-lg pl-9 pr-3 py-2.5 text-sm text-[#0A2242] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
            style={{ fontSize: '16px' }}
            data-testid="player-search-input"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (() => {
          const q = search.trim().toLowerCase();
          const filtered = q
            ? players.filter(p => {
                const haystack = [
                  p.name,
                  p.email,
                  p.profile?.current_team,
                  p.profile?.position,
                  String(p.profile?.birth_year || ''),
                  p.profile?.phone,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(q);
              })
            : players;
          return filtered.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center shadow-sm">
              <p className="text-[#64748B] text-sm">
                {q ? `No players match "${search}".` : (birthYear !== 'all' ? `No players born in ${birthYear}.` : 'No players yet.')}
              </p>
            </div>
          ) : (
          <div className="space-y-2">
            {q && (
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] px-1">
                {filtered.length} of {players.length} match "{search}"
              </p>
            )}
            {filtered.map((player, i) => {
              const isIncomplete = !player.onboarding_complete;
              return (
                <motion.div
                  key={player.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className={`bg-white border rounded-xl p-4 shadow-sm ${
                    isIncomplete ? 'border-[#FF9100]/30 opacity-70' : 'border-[#E2E8F0]'
                  }`}
                  data-testid={`player-item-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(`/coach/player/${player.id}`)}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
                        <User size={18} className="text-[#94A3B8]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-['Barlow_Condensed'] text-sm font-bold truncate text-[#0A2242]">{player.name}</h4>
                          {isIncomplete && (
                            <span className="text-[8px] bg-[#FF9100]/10 text-[#FF9100] px-1.5 py-0.5 rounded-full font-bold uppercase">Incomplete</span>
                          )}
                        </div>
                        <p className="text-[10px] text-[#94A3B8] truncate">{player.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {player.profile?.birth_year && (
                            <span className="text-[9px] text-[#2563EB] font-bold">{player.profile.birth_year}</span>
                          )}
                          {player.profile?.position && (
                            <span className="text-[9px] text-[#64748B] font-bold uppercase">{player.profile.position}</span>
                          )}
                          <span className="text-[9px] text-[#94A3B8] flex items-center gap-0.5">
                            <Clock size={8} /> {new Date(player.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-[#CBD5E1] flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/coach/messages/${player.id}`)}
                        className="p-2 rounded-lg text-[#2563EB] hover:bg-[#2563EB]/10 transition-colors"
                        title={`Message ${player.name}`}
                        data-testid={`message-player-${i}`}
                      >
                        <MessageSquare size={14} />
                      </button>
                      <button
                        onClick={() => toggleSub(player.id, player.subscription_active)}
                        className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1 ${
                          player.subscription_active
                            ? 'bg-[#10B981]/10 text-[#10B981] hover:bg-red-500/10 hover:text-red-400'
                            : 'bg-[#F1F5F9] text-[#94A3B8] hover:bg-[#2563EB]/10 hover:text-[#2563EB]'
                        }`}
                        data-testid={`toggle-sub-${i}`}
                      >
                        {player.subscription_active ? <><Check size={10} /> Active</> : <><X size={10} /> Inactive</>}
                      </button>
                      <button
                        onClick={() => resetPlayer(player.id, player.name)}
                        className="p-2 rounded-lg text-[#94A3B8] hover:text-[#FF9100] hover:bg-[#FF9100]/10 transition-colors"
                        title={`Reset ${player.name} to Week 1`}
                        data-testid={`reset-player-${i}`}
                      >
                        <RotateCcw size={14} />
                      </button>
                      <button
                        onClick={() => deletePlayer(player.id, player.name)}
                        className="p-2 rounded-lg text-[#94A3B8] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        data-testid={`delete-player-${i}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          );
        })()}
      </div>
    </Layout>
  );
}
